import { parseBytes32String } from '@ethersproject/strings'
import { useMemo } from 'react'

import { useTokenCatalog } from '@/data/marketApi/hooks/useTokenCatalog'
import { catalogEntryToToken } from '@/features/tokens/tokenCatalogRuntime'
import { getNativeCurrency, isNativeCurrencySymbol } from '../config/native'
import { Currency, Token, currencyEquals } from '../sdk'
import { NEVER_RELOAD, useSingleCallResult } from '../state/multicall/hooks'
import { useUserAddedTokens } from '../state/user/hooks'
import { isAddress } from '../utils'

import { useAppWeb3 } from './index'
import { useBytes32TokenContract, useTokenContract } from './useContract'

/**
 * Mapa de tokens del catálogo de producto + tokens importados por el usuario.
 */
export function useAllTokens(): { [address: string]: Token } {
  const { chainId } = useAppWeb3()
  const userAddedTokens = useUserAddedTokens()
  const { entries } = useTokenCatalog()

  return useMemo(() => {
    if (!chainId) return {}
    const tokenMap: { [address: string]: Token } = {}
    for (const entry of entries) {
      if (entry.role === 'erc20' || entry.role === 'stablecoin') {
        const token = catalogEntryToToken(chainId, entry)
        tokenMap[token.address] = token
      }
    }
    for (const token of userAddedTokens) {
      tokenMap[token.address] = token
    }
    return tokenMap
  }, [chainId, entries, userAddedTokens])
}

export function useIsUserAddedToken(currency: Currency): boolean {
  const userAddedTokens = useUserAddedTokens()
  return !!userAddedTokens.find(token => currencyEquals(currency, token))
}

/** Tokens del catálogo oficial (no muestran controles de importación en el modal legacy). */
export function useHideTokenRowControls(currency: Currency): boolean {
  const { chainId } = useAppWeb3()
  const { entries } = useTokenCatalog()
  const address = currency instanceof Token ? currency.address : null
  return useMemo(() => {
    if (!chainId || !address) return false
    const lower = address.toLowerCase()
    return entries.some(
      (entry) =>
        (entry.role === 'erc20' || entry.role === 'stablecoin') &&
        (entry.onChainAddress?.toLowerCase() === lower || entry.catalogAddress.toLowerCase() === lower),
    )
  }, [chainId, address, entries])
}

// parse a name or symbol from a token response
const BYTES32_REGEX = /^0x[a-fA-F0-9]{64}$/
function parseStringOrBytes32(str: string | undefined, bytes32: string | undefined, defaultValue: string): string {
  return str && str.length > 0
    ? str
    : bytes32 && BYTES32_REGEX.test(bytes32)
    ? parseBytes32String(bytes32)
    : defaultValue
}

// undefined if invalid or does not exist
// null if loading
// otherwise returns the token
export function useToken(tokenAddress?: string): Token | undefined | null {
  const { chainId } = useAppWeb3()
  const tokens = useAllTokens()

  const address = isAddress(tokenAddress)

  const tokenContract = useTokenContract(address ? address : undefined, false)
  const tokenContractBytes32 = useBytes32TokenContract(address ? address : undefined, false)
  const token: Token | undefined = address ? tokens[address] : undefined

  const tokenName = useSingleCallResult(token ? undefined : tokenContract, 'name', undefined, NEVER_RELOAD)
  const tokenNameBytes32 = useSingleCallResult(
    token ? undefined : tokenContractBytes32,
    'name',
    undefined,
    NEVER_RELOAD
  )
  const symbol = useSingleCallResult(token ? undefined : tokenContract, 'symbol', undefined, NEVER_RELOAD)
  const symbolBytes32 = useSingleCallResult(token ? undefined : tokenContractBytes32, 'symbol', undefined, NEVER_RELOAD)
  const decimals = useSingleCallResult(token ? undefined : tokenContract, 'decimals', undefined, NEVER_RELOAD)

  return useMemo(() => {
    if (token) return token
    if (!chainId || !address) return undefined
    if (decimals.loading || symbol.loading || tokenName.loading) return null
    if (decimals.result) {
      return new Token(
        chainId,
        address,
        decimals.result[0],
        parseStringOrBytes32(symbol.result?.[0], symbolBytes32.result?.[0], 'UNKNOWN'),
        parseStringOrBytes32(tokenName.result?.[0], tokenNameBytes32.result?.[0], 'Unknown Token')
      )
    }
    return undefined
  }, [
    address,
    chainId,
    decimals.loading,
    decimals.result,
    symbol.loading,
    symbol.result,
    symbolBytes32.result,
    token,
    tokenName.loading,
    tokenName.result,
    tokenNameBytes32.result
  ])
}

export function useCurrency(currencyId: string | undefined): Currency | null | undefined {
  const { chainId } = useAppWeb3()
  const isNative = chainId != null && isNativeCurrencySymbol(currencyId, chainId)
  const token = useToken(isNative ? undefined : currencyId)
  return isNative && chainId != null ? getNativeCurrency(chainId) : token
}
